import {
  applyColumnWidthMultiplier,
  applyDefaultMinWidths,
  getColumnWidthMultiplier,
  getFontSizePx,
  getHeaderHeight,
  getRowHeight,
} from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import useTableUIStore from "@/store/tableUIStore";
import {
  DetailGridInfo,
  GridReadyEvent,
  IRowNode,
  RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { useTheme } from "next-themes";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { returnCreationTableDetailColDefs } from "./helper/colDefs/creationTableDetailColDefs";
import { defaultColDefTradeCreation } from "./helper/colDefs/defaultColDefTradeCreation";
import {
  blockGridStylesTradeCreation,
  getDetailRowHeight,
} from "./helper/tradeCreationHelper";

interface IAllocation {
  basisRisk: number;
  complianceStatus: string;
  dV01: number;
  id: number;
  inflationRisk: number;
  notionalLeg1: number;
  notionalLeg2: number | null;
  portfolio: string;
  creditSpreadRisk: number;
}

const CreationDetailTable = ({ data, node, api }: CustomCellRendererProps) => {
  const { theme } = useTheme();
  const tableStripeOddTrue = useTableUIStore(
    (state) => state.tableStripeImportOn,
  ); // used to change the table stripe with settings.
  const fontSize = useTableUIStore((state) => state.fontSize);
  const rowHeightSetting = useTableUIStore((state) => state.rowHeight);
  const creationFlashColorLight = useTableUIStore(
    (state) => state.creationFlashColorLight,
  );
  const creationFlashColorDark = useTableUIStore(
    (state) => state.creationFlashColorDark,
  );
  const dynamicRowHeight = useMemo(() => getRowHeight(rowHeightSetting), [rowHeightSetting]);
  const dynamicHeaderHeight = useMemo(() => getHeaderHeight(rowHeightSetting), [rowHeightSetting]);
  const columnWidthMultiplier = useMemo(() => getColumnWidthMultiplier(fontSize), [fontSize]);
  const rowHeightNum = getDetailRowHeight(data.allocations.length, dynamicRowHeight);
  const containerStyle = useMemo(
    () => ({ width: "100%", height: rowHeightNum }),
    [rowHeightNum],
  );
  const rowId = node.id!;

  useEffect(() => {
    return () => {
      if (!api.isDestroyed()) {
        api.removeDetailGridInfo(rowId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For task 15143 -> Need to map the securityType to the allocations.
  const rowData = data.allocations.map((alloc: IAllocation) => {
    return { ...alloc, securityType: data.securityType };
  });
  const colDefs = useMemo(() => {
    const cols = applyDefaultMinWidths(returnCreationTableDetailColDefs(data));
    return applyColumnWidthMultiplier(cols, columnWidthMultiplier);
  }, [data, columnWidthMultiplier]);

  const onGridReady = (params: GridReadyEvent) => {
    const gridInfo: DetailGridInfo = {
      id: rowId,
      api: params.api,
    };
    api.addDetailGridInfo(rowId, gridInfo);
    const masterNodeId = node.id?.split("_").pop()?.toString();
    const masterNode = api.getRowNode(masterNodeId!);
    if (masterNode?.isSelected()) {
      if (rowData && rowData.length > 0) {
        gridInfo.api?.selectAll();
      }
    }
  };

  interface IRowNodeNullSelected extends IRowNode {
    setSelected(_newValue?: boolean | null): void;
  }
  const onRowSelected = useCallback((params: RowSelectedEvent) => {
    const masterNodeId: string | undefined = node.id?.split("_").pop();
    const masterNode: IRowNodeNullSelected | undefined = api.getRowNode(
      masterNodeId!,
    );
    const selectedNode = params.node;
    const allSelectedNodes = params.api.getSelectedNodes();
    //If all allocations are selected, set master row to be selected.
    //Otherwise, set master row to be partially selected.
    if (masterNode) {
      if (selectedNode.isSelected() == true) {
        if (allSelectedNodes.length != data.allocations.length) {
          masterNode.setSelected(null);
        } else {
          masterNode.setSelected(true);
        }
      } else {
        if (allSelectedNodes.length == 0) {
          masterNode.setSelected(false);
        } else {
          masterNode.setSelected(null);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const style = useMemo(
    () => ({
      ...(blockGridStylesTradeCreation(
        theme,
        tableStripeOddTrue,
      ) as React.CSSProperties),
      fontSize: `${getFontSizePx(fontSize)}px`,
    }),
    [theme, tableStripeOddTrue, fontSize, creationFlashColorLight, creationFlashColorDark],
  );

  const popupParent = useMemo<HTMLElement | null>(() => {
    return document.querySelector(".GridContainer");
  }, []);

  return (
    <div
      style={containerStyle}
      className="flex h-full w-full flex-row bg-cubeBase-500 dark:bg-cubeBaseDark-500"
    >
      <div
        className={`${theme == "dark" ? "ag-theme-balham-dark" : "ag-theme-balham"}  cssStylingOnNestedTable1 h-full w-full`}
        style={style}
      >
        <AgGridReact
          data-id="detailGrid"
          columnDefs={colDefs}
          rowData={rowData}
          onGridReady={onGridReady}
          onRowSelected={onRowSelected}
          rowSelection={"multiple"}
          suppressRowClickSelection={true}
          defaultColDef={defaultColDefTradeCreation}
          suppressMovableColumns={true}
          rowHeight={dynamicRowHeight}
          headerHeight={dynamicHeaderHeight}
          popupParent={popupParent}
        />
      </div>
    </div>
  );
};

export default memo(CreationDetailTable);
